// SkillOutputRenderer — detecta tipo do output e renderiza apropriado.
//
// Pipeline de detecção (em ordem, primeiro match ganha):
//   STRINGS:
//     - URL/data-URL de imagem  → ImageDisplay (clicável → lightbox)
//     - URL de documento (.pdf/.docx/.zip/...) → FileLinkDisplay (Abrir/Baixar)
//     - URL http(s) genérica    → UrlDisplay (link clicável)
//     - HTML inline             → DocumentDisplay (iframe sandbox)
//     - Markdown (heurística)   → MarkdownDisplay (react-markdown + remark-gfm)
//     - Texto simples           → TextDisplay (copiável)
//   OBJETOS (shapes específicos):
//     - { slides: [...] }                 → SlidesDisplay (cards lado-a-lado)
//     - { image_url, caption } / { src }  → ImageDisplay com legenda
//     - { document_html } / { html }      → DocumentDisplay
//     - { markdown }                      → MarkdownDisplay
//     - { artifacts: [...] }              → grid de artifacts
//     - resto                             → JsonObjectDisplay (fallback)
//   ARRAYS:
//     - galeria de imagens (objs com image_url ou strings de URL) → GalleryDisplay
//     - resto → loop renderValue
//
// Defesa: cada renderer roda dentro de SafeRender (try/catch) — se quebrar,
// cai pra TextDisplay com a string serializada. UI nunca fica em branco.

import { Component, useState, type ReactElement, type ReactNode} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Image as ImageIcon,
  FileText,
  File as FileIcon,
  Download,
  ExternalLink,
  Copy,
  Check,
  Code as CodeIcon,
  ChevronDown,
  ChevronRight,
  Layers,
  AlertTriangle,
  Music,
} from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { isSkillArtifactOrGroup, type SkillArtifact, type SkillArtifactOrGroup, type SkillArtifactMediaGroup } from '@/lib/types/skill-artifact';
import { fmtNumber } from '@/lib/format';

const IMAGE_EXT = /\.(png|jpe?g|webp|gif|svg|bmp|avif)(\?|$|#)/i;
const DOC_EXT = /\.(pdf|docx?|xlsx?|pptx?|zip|rar|7z|tar|gz|csv|txt|md|json|xml|yaml|yml)(\?|$|#)/i;
const URL_REGEX = /^https?:\/\//i;
const DATA_URL_IMAGE = /^data:image\//i;
const HTML_TAG_REGEX = /<\/?(html|body|div|h[1-6]|p|span|table|ul|ol|li|article|section|main|header|footer|figure|img)\b/i;

export interface SkillOutputRendererProps {
  output: unknown;
  /** Quando dentro de aba (default), aplica padding/scroll. */
  contained?: boolean;
}

export function SkillOutputRenderer({ output, contained = true }: SkillOutputRendererProps) {
  if (output === null || output === undefined) {
    return <p className="text-sm text-muted-foreground italic">Sem output.</p>;
  }

  const wrapperClass = contained ? 'space-y-4 max-h-[60vh] overflow-y-auto' : 'space-y-4';

  return <div className={wrapperClass}>{renderValue(output, 'output')}</div>;
}

// =============================================================================
// Roteador principal por shape
// =============================================================================
function renderValue(value: unknown, key: string): ReactElement {
  if (value === null || value === undefined) {
    return (
      <div key={key} className="text-xs text-muted-foreground italic">
        (vazio)
      </div>
    );
  }

  // STRINGS
  if (typeof value === 'string') {
    return (
      <SafeRender key={key} fallbackText={value}>
        {renderString(value)}
      </SafeRender>
    );
  }

  // ARRAYS
  if (Array.isArray(value)) {
    return (
      <SafeRender key={key} fallbackText={safeStringify(value)}>
        {renderArray(value, key)}
      </SafeRender>
    );
  }

  // OBJETOS
  if (typeof value === 'object') {
    return (
      <SafeRender key={key} fallbackText={safeStringify(value)}>
        {renderObject(value as Record<string, unknown>, key)}
      </SafeRender>
    );
  }

  // Number, boolean
  return (
    <div key={key} className="text-sm font-mono bg-muted px-2 py-1 rounded inline-block">
      {String(value)}
    </div>
  );
}

function renderString(value: string): ReactElement {
  // URL/data-URL de imagem
  if ((URL_REGEX.test(value) && IMAGE_EXT.test(value)) || DATA_URL_IMAGE.test(value)) {
    return <ImageDisplay src={value} />;
  }
  // URL de documento conhecido
  if (URL_REGEX.test(value) && DOC_EXT.test(value)) {
    return <FileLinkDisplay url={value} />;
  }
  // URL genérica
  if (URL_REGEX.test(value)) {
    return <UrlDisplay url={value} />;
  }
  // HTML
  if (HTML_TAG_REGEX.test(value)) {
    return <DocumentDisplay content={value} contentType="html" />;
  }
  // Markdown (heurística — evita strings de 1 linha curtas)
  if (looksLikeMarkdown(value)) {
    return <MarkdownDisplay content={value} />;
  }
  // Texto simples
  return <TextDisplay text={value} />;
}

function renderArray(value: unknown[], key: string): ReactElement {
  // Galeria de imagens — array só de imagens (objetos {image_url}/{src} ou strings URL)
  if (value.length > 0 && value.every(isImageLike)) {
    return <GalleryDisplay items={value} />;
  }

  return (
    <div key={key} className="space-y-2">
      <div className="text-xs uppercase font-semibold text-muted-foreground">
        Lista ({value.length} item{value.length !== 1 ? 's' : ''})
      </div>
      <div className="space-y-2 pl-3 border-l-2 border-border">
        {value.map((item, i) => renderValue(item, `${key}[${i}]`))}
      </div>
    </div>
  );
}

function renderObject(obj: Record<string, unknown>, key: string): ReactElement {
  // { slides: [...] }
  if (Array.isArray(obj.slides) && obj.slides.length > 0) {
    return <SlidesDisplay slides={obj.slides as Array<Record<string, unknown>>} />;
  }
  // { artifacts: [...] }
  if (Array.isArray(obj.artifacts) && obj.artifacts.length > 0) {
    return <ArtifactsGrid artifacts={obj.artifacts} />;
  }
  // { image_url, caption? } ou { src, alt? }
  const imgUrl = pickString(obj, ['image_url', 'src', 'url']);
  if (imgUrl && (IMAGE_EXT.test(imgUrl) || DATA_URL_IMAGE.test(imgUrl) || obj.image_url)) {
    const caption = pickString(obj, ['caption', 'alt', 'title', 'description']);
    return <ImageDisplay src={imgUrl} caption={caption ?? undefined} />;
  }
  // { document_html } / { html }
  const html = pickString(obj, ['document_html', 'html']);
  if (html) {
    return <DocumentDisplay content={html} contentType="html" />;
  }
  // { markdown }
  const md = pickString(obj, ['markdown', 'md']);
  if (md) {
    return <MarkdownDisplay content={md} />;
  }
  // Fallback
  return <JsonObjectDisplay obj={obj} parentKey={key} />;
}

// =============================================================================
// Heurísticas
// =============================================================================
function looksLikeMarkdown(s: string): boolean {
  if (s.length < 4) return false;
  const lines = s.split('\n');
  // String curta de 1 linha sem markers fortes não vira MD
  if (lines.length === 1) {
    return /^#{1,6}\s.+/.test(s) || /^\s*[-*]\s.+/.test(s);
  }
  // Múltiplas linhas: procura por headers, bullets, code-fence, tabela
  return (
    /(^|\n)#{1,6}\s/.test(s) ||
    /(^|\n)\s*[-*+]\s/.test(s) ||
    /(^|\n)\s*\d+\.\s/.test(s) ||
    /```/.test(s) ||
    /(^|\n)\|.+\|/.test(s) ||
    /\*\*[^*]+\*\*/.test(s)
  );
}

function isImageLike(item: unknown): boolean {
  if (typeof item === 'string') {
    return (URL_REGEX.test(item) && IMAGE_EXT.test(item)) || DATA_URL_IMAGE.test(item);
  }
  if (typeof item === 'object' && item !== null) {
    const obj = item as Record<string, unknown>;
    const url = pickString(obj, ['image_url', 'src', 'url']);
    if (!url) return false;
    return IMAGE_EXT.test(url) || DATA_URL_IMAGE.test(url) || !!obj.image_url;
  }
  return false;
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

// =============================================================================
// SafeRender — envelope try/catch via ErrorBoundary
// =============================================================================
class SafeRender extends Component<
  { children: ReactNode; fallbackText: string },
  { hasError: boolean; errorMsg: string }
> {
  state = { hasError: false, errorMsg: '' };

  static getDerivedStateFromError(err: Error) {
    return { hasError: true, errorMsg: err.message ?? String(err) };
  }

  componentDidCatch(err: Error) {
    // Log silencioso — não queremos quebrar a UI.
    console.warn('[SkillOutputRenderer] renderer falhou:', err);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-3.5 w-3.5" />
            Renderizador falhou ({this.state.errorMsg}). Mostrando fallback bruto.
          </div>
          <TextDisplay text={this.props.fallbackText} />
        </div>
      );
    }
    return this.props.children;
  }
}

// =============================================================================
// ImageDisplay — clique amplia, opcional caption
// =============================================================================
function ImageDisplay({ src, caption }: { src: string; caption?: string }) {
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const filename = extractFilename(src) || 'imagem';

  if (imgError) {
    // Imagem 404/inválida → cai pra link clicável
    return (
      <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg p-3">
        <div className="flex items-center gap-2 text-xs text-amber-900 dark:text-amber-200 mb-1">
          <AlertTriangle className="h-3.5 w-3.5" />
          Imagem não carregou. Link bruto:
        </div>
        <a href={src} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">
          {src}
        </a>
      </div>
    );
  }

  return (
    <>
      <div className="border border-border rounded-lg p-3 bg-muted/30 inline-block max-w-full">
        <div className="flex items-center gap-2 mb-2">
          <ImageIcon className="h-4 w-4 text-blue-500" />
          <span className="text-xs font-medium truncate max-w-[400px]">{filename}</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="block group"
          title="Clique para ampliar"
        >
          <img
            src={src}
            alt={caption || filename}
            onError={() => setImgError(true)}
            className="max-h-60 max-w-full rounded border border-border group-hover:opacity-90 transition"
          />
        </button>
        {caption && (
          <p className="text-xs text-muted-foreground mt-2 max-w-[400px]">{caption}</p>
        )}
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="default" onClick={() => setOpen(true)}>
            <ExternalLink className="h-3.5 w-3.5" />
            Abrir
          </Button>
          <Button size="sm" variant="default" asChild>
            <a href={src} download={filename} target="_blank" rel="noopener noreferrer">
              <Download className="h-3.5 w-3.5" />
              Baixar
            </a>
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-auto p-3">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-blue-500" />
              <span className="truncate">{filename}</span>
              <Button size="sm" variant="default" asChild className="ml-auto">
                <a href={src} download={filename} target="_blank" rel="noopener noreferrer">
                  <Download className="h-3.5 w-3.5" />
                  Baixar
                </a>
              </Button>
            </DialogTitle>
          </DialogHeader>
          <img src={src} alt={caption || filename} className="max-w-full mx-auto" />
          {caption && <p className="text-sm text-muted-foreground mt-3 text-center">{caption}</p>}
        </DialogContent>
      </Dialog>
    </>
  );
}

// =============================================================================
// FileLinkDisplay — arquivo (.pdf/.docx/...) com Abrir/Baixar
// =============================================================================
function FileLinkDisplay({ url }: { url: string }) {
  const filename = extractFilename(url) || 'arquivo';
  const ext = (filename.split('.').pop() || '').toLowerCase();
  return (
    <div className="border border-border rounded-lg p-3 bg-muted/30 inline-block max-w-full">
      <div className="flex items-center gap-2 mb-2">
        <FileIcon className="h-4 w-4 text-purple-500" />
        <span className="text-xs font-medium">
          Arquivo: <span className="font-mono">{filename}</span>
          {ext && <span className="ml-1 text-muted-foreground uppercase">({ext})</span>}
        </span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="default" asChild>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5" />
            Abrir
          </a>
        </Button>
        <Button size="sm" variant="default" asChild>
          <a href={url} download={filename} target="_blank" rel="noopener noreferrer">
            <Download className="h-3.5 w-3.5" />
            Baixar
          </a>
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// DocumentDisplay — HTML com iframe sandbox; markdown roteia pra MarkdownDisplay
// =============================================================================
function DocumentDisplay({
  content,
  contentType,
}: {
  content: string;
  contentType: 'html' | 'markdown';
}) {
  const [open, setOpen] = useState(false);
  const filename = `documento.${contentType === 'html' ? 'html' : 'md'}`;
  const blob = new Blob([content], { type: contentType === 'html' ? 'text/html' : 'text/markdown' });
  const url = URL.createObjectURL(blob);

  return (
    <>
      <div className="border border-border rounded-lg p-3 bg-muted/30">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-4 w-4 text-purple-500" />
          <span className="text-xs font-medium">
            Documento {contentType === 'html' ? 'HTML' : 'Markdown'} ({content.length.toLocaleString('pt-BR')} chars)
          </span>
        </div>
        {contentType === 'html' ? (
          <iframe
            srcDoc={content}
            title="Preview HTML"
            sandbox="allow-same-origin"
            className="w-full min-h-[200px] max-h-[400px] bg-white rounded border"
          />
        ) : (
          <MarkdownInline content={content.slice(0, 2000)} />
        )}
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="default" onClick={() => setOpen(true)}>
            <ExternalLink className="h-3.5 w-3.5" />
            Abrir ampliado
          </Button>
          <Button size="sm" variant="default" asChild>
            <a href={url} download={filename}>
              <Download className="h-3.5 w-3.5" />
              Baixar
            </a>
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-500" />
              {filename}
              <Button size="sm" variant="default" asChild className="ml-auto">
                <a href={url} download={filename}>
                  <Download className="h-3.5 w-3.5" />
                  Baixar
                </a>
              </Button>
            </DialogTitle>
          </DialogHeader>
          {contentType === 'html' ? (
            <iframe
              srcDoc={content}
              title="Preview HTML"
              sandbox="allow-same-origin"
              className="w-full min-h-[60vh] bg-white rounded border"
            />
          ) : (
            <MarkdownInline content={content} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// =============================================================================
// MarkdownDisplay — render via react-markdown + remark-gfm
// =============================================================================
function MarkdownDisplay({ content }: { content: string }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-muted/30">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-purple-500" />
        <span className="text-xs font-medium text-muted-foreground">
          Markdown ({content.length.toLocaleString('pt-BR')} chars)
        </span>
      </div>
      <MarkdownInline content={content} />
    </div>
  );
}

// Wrapper interno com classes prose + componentes safe
function MarkdownInline({ content }: { content: string }) {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children, ...props }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <img src={src} alt={alt ?? ''} className="max-w-full rounded border border-border" />
          ),
          code: ({ className, children, ...props }) => (
            <code className={`${className ?? ''} bg-muted px-1.5 py-0.5 rounded text-xs`} {...props}>
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">{children}</pre>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-border">{children}</table>
            </div>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// =============================================================================
// SlidesDisplay — cards lado-a-lado pra { slides: [...] }
// =============================================================================
function SlidesDisplay({ slides }: { slides: Array<Record<string, unknown>> }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs uppercase font-semibold text-muted-foreground">
        <Layers className="h-3.5 w-3.5" />
        Slides ({slides.length})
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {slides.map((slide, i) => (
          <SlideCard key={i} slide={slide} index={i} />
        ))}
      </div>
    </div>
  );
}

function SlideCard({ slide, index }: { slide: Record<string, unknown>; index: number }) {
  const imageUrl = pickString(slide, ['image_url', 'image', 'src', 'url']);
  const caption = pickString(slide, ['caption', 'text', 'description', 'content']);
  const cta = pickString(slide, ['cta', 'call_to_action', 'button_text']);
  const ctaUrl = pickString(slide, ['cta_url', 'link', 'href']);
  const title = pickString(slide, ['title', 'headline', 'name']);

  return (
    <div className="border border-border rounded-lg p-3 bg-card flex flex-col gap-2">
      <div className="text-[10px] uppercase font-semibold text-muted-foreground">
        Slide {index + 1}
      </div>
      {title && <div className="text-sm font-semibold">{title}</div>}
      {imageUrl && (
        <SafeRender fallbackText={imageUrl}>
          <ImageDisplay src={imageUrl} />
        </SafeRender>
      )}
      {caption && (
        <p className="text-xs text-foreground whitespace-pre-wrap break-words">{caption}</p>
      )}
      {cta && (
        <Button size="sm" variant="default" asChild={!!ctaUrl} className="self-start">
          {ctaUrl ? (
            <a href={ctaUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
              {cta}
            </a>
          ) : (
            <span>{cta}</span>
          )}
        </Button>
      )}
      {/* Campos extras não-cobertos vão pro fallback collapsable */}
      <ExtraFields
        obj={slide}
        knownKeys={['image_url', 'image', 'src', 'url', 'caption', 'text', 'description', 'content', 'cta', 'call_to_action', 'button_text', 'cta_url', 'link', 'href', 'title', 'headline', 'name']}
      />
    </div>
  );
}

// =============================================================================
// GalleryDisplay — array de imagens
// =============================================================================
function GalleryDisplay({ items }: { items: unknown[] }) {
  const images: Array<{ src: string; caption?: string }> = items
    .map((it) => {
      if (typeof it === 'string') return { src: it };
      if (typeof it === 'object' && it !== null) {
        const obj = it as Record<string, unknown>;
        const src = pickString(obj, ['image_url', 'src', 'url']);
        if (!src) return null;
        const caption = pickString(obj, ['caption', 'alt', 'title']) ?? undefined;
        return { src, caption };
      }
      return null;
    })
    .filter((x): x is { src: string; caption?: string } => x !== null);

  if (images.length === 0) return <></>;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs uppercase font-semibold text-muted-foreground">
        <ImageIcon className="h-3.5 w-3.5" />
        Galeria ({images.length} imagens)
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {images.map((img, i) => (
          <SafeRender key={i} fallbackText={img.src}>
            <ImageDisplay src={img.src} caption={img.caption} />
          </SafeRender>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// ArtifactsGrid — renderiza output.artifacts[] como cards
//
// Onda Companion-Callback: dispatcha por `type` discriminado (image, document,
// video, audio, media_group) usando ArtifactDisplay/AudioDisplay/VideoPlayer.
// Outputs antigos sem `type` field caem no fallback renderValue (heuristica
// existente). Compat 100%.
// =============================================================================
function ArtifactsGrid({ artifacts }: { artifacts: unknown[] }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs uppercase font-semibold text-muted-foreground">
        <FileIcon className="h-3.5 w-3.5" />
        Arquivos gerados ({artifacts.length})
      </div>
      {/* SKL-D r3: célula SEM borda — ImageDisplay/ArtifactDisplay já desenham
          o próprio card (borda dupla, reclamação do admin). */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {artifacts.map((art, i) => (
          <div key={i} className="min-w-0">
            <SafeRender fallbackText={`artifact[${i}]`}>
              {isSkillArtifactOrGroup(art) ? (
                <ArtifactDisplay artifact={art as SkillArtifactOrGroup} />
              ) : (
                renderValue(art, `artifact[${i}]`)
              )}
            </SafeRender>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// ArtifactDisplay — discriminator type-aware (Onda Companion-Callback)
// =============================================================================
function ArtifactDisplay({ artifact }: { artifact: SkillArtifactOrGroup }) {
  if (artifact.type === 'media_group') {
    return <MediaGroupDisplay group={artifact as SkillArtifactMediaGroup} />;
  }

  const a = artifact as SkillArtifact;
  switch (a.type) {
    case 'image':
      return <ImageDisplay src={a.signed_url} caption={a.caption} />;
    case 'video':
      return <VideoPlayer src={a.signed_url} name={a.name} caption={a.caption} bytes={a.bytes} />;
    case 'audio':
      return <AudioDisplay src={a.signed_url} name={a.name} caption={a.caption} bytes={a.bytes} />;
    case 'document':
      return <FileLinkDisplay url={a.signed_url} />;
    default:
      return <JsonObjectDisplay obj={a as unknown as Record<string, unknown>} parentKey="artifact" />;
  }
}

// =============================================================================
// MediaGroupDisplay — carrosseis (2-10 imagens/videos)
// =============================================================================
function MediaGroupDisplay({ group }: { group: SkillArtifactMediaGroup }) {
  const isAllImages = group.items.every((i) => i.type === 'image');

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs uppercase font-semibold text-muted-foreground">
        <Layers className="h-3.5 w-3.5" />
        Media Group ({group.items.length} items)
      </div>
      {group.caption && (
        <p className="text-xs text-muted-foreground">{group.caption}</p>
      )}
      <div className={isAllImages ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3' : 'space-y-3'}>
        {group.items.map((item, i) => (
          <SafeRender key={i} fallbackText={item.signed_url}>
            <ArtifactDisplay artifact={item} />
          </SafeRender>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// AudioDisplay — audio HTML5 inline
// =============================================================================
function AudioDisplay({
  src,
  name,
  caption,
  bytes,
}: {
  src: string;
  name: string;
  caption?: string;
  bytes?: number;
}) {
  const sizeMb = bytes ? fmtNumber(bytes / 1024 / 1024, 1) : null;
  return (
    <div className="border border-border rounded-lg p-3 bg-muted/30">
      <div className="flex items-center gap-2 mb-2">
        <Music className="h-4 w-4 text-emerald-500" />
        <span className="text-xs font-medium truncate max-w-[400px]">
          {name}
          {sizeMb && <span className="ml-1 text-muted-foreground">({sizeMb}MB)</span>}
        </span>
      </div>
      <audio src={src} controls preload="metadata" className="w-full" />
      {caption && (
        <p className="text-xs text-muted-foreground mt-2">{caption}</p>
      )}
      <div className="flex gap-2 mt-2">
        <Button size="sm" variant="default" asChild>
          <a href={src} download={name} target="_blank" rel="noopener noreferrer">
            <Download className="h-3.5 w-3.5" />
            Baixar
          </a>
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// ExtraFields — chaves não-cobertas pelo SlideCard, em <details>
// =============================================================================
function ExtraFields({ obj, knownKeys }: { obj: Record<string, unknown>; knownKeys: string[] }) {
  const extras = Object.entries(obj).filter(([k]) => !knownKeys.includes(k));
  if (extras.length === 0) return null;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
        +{extras.length} campo{extras.length !== 1 ? 's' : ''} extra
      </summary>
      <div className="mt-2 space-y-1">
        {extras.map(([k, v]) => (
          <div key={k}>
            <span className="font-mono font-semibold text-purple-700 dark:text-purple-300">{k}:</span>{' '}
            <span className="font-mono">{typeof v === 'string' ? v : safeStringify(v)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

// =============================================================================
// TextDisplay — texto puro com Copiar
// =============================================================================
function TextDisplay({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback silencioso
    }
  };

  const isLong = text.length > 1500;

  return (
    <div className="border border-border rounded-lg p-3 bg-muted/30">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">
          Texto ({text.length.toLocaleString('pt-BR')} chars)
        </span>
        <Button size="sm" variant="default" onClick={handleCopy}>
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              Copiado!
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copiar
            </>
          )}
        </Button>
      </div>
      <pre className={`text-sm whitespace-pre-wrap break-words font-sans ${isLong ? 'max-h-96 overflow-y-auto' : ''}`}>
        {text}
      </pre>
    </div>
  );
}

// =============================================================================
// UrlDisplay — link clicável
// =============================================================================
function UrlDisplay({ url }: { url: string }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-muted/30">
      <div className="flex items-center gap-2">
        <ExternalLink className="h-4 w-4 text-green-600" />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-mono text-blue-600 hover:underline break-all"
        >
          {url}
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// JsonObjectDisplay — viewer collapsable (fallback)
// =============================================================================
function JsonObjectDisplay({ obj, parentKey }: { obj: Record<string, unknown>; parentKey: string }) {
  const [open, setOpen] = useState(true);
  const keys = Object.keys(obj);

  return (
    <div className="border border-border rounded-lg p-3 bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2 hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <CodeIcon className="h-3.5 w-3.5" />
        Objeto ({keys.length} chave{keys.length !== 1 ? 's' : ''}: {keys.slice(0, 5).join(', ')}
        {keys.length > 5 && '…'})
      </button>
      {open && (
        <div className="space-y-2 pl-4 border-l-2 border-border">
          {keys.map((k) => (
            <div key={k}>
              <div className="text-xs font-mono font-semibold text-purple-700 dark:text-purple-300 mb-1">
                {k}:
              </div>
              <div className="pl-3">{renderValue(obj[k], `${parentKey}.${k}`)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function extractFilename(url: string): string | null {
  try {
    const parsed = new URL(url);
    const parts = parsed.pathname.split('/');
    return parts[parts.length - 1] || null;
  } catch {
    const parts = url.split('/');
    return parts[parts.length - 1] || null;
  }
}
